import { ReturnFlow } from "@redotech/redo-return-core-common/return-flow";
import { flowElementIdsOfSteps } from "@redotech/redo-return-core-common/return-flow/flow-element-id-spec";
import {
  FlowElementTextOverlay,
  FlowElementTranslatableField,
} from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import { createContext, useCallback, useContext } from "react";
import { localizeCustomerPortalFlowText } from "./util";

/** The empty default falls every lookup through to the built-in localization. */
export const FlowElementTextOverlayContext =
  createContext<FlowElementTextOverlay>({});

/**
 * Display-only, per the invariant on FlowElementTextOverlay: the merchant's
 * translation, else the Redo-default localization, else the English text.
 */
export function flowElementDisplayText(
  overlay: FlowElementTextOverlay,
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  text: string,
): string {
  const translated = elementId ? overlay[elementId]?.[field] : undefined;
  return translated || localizeCustomerPortalFlowText(text);
}

/**
 * Overlay first. Merchant custom copy stays canonical. Redo defaults keep t().
 */
export function flowElementCustomOrDefaultText(
  overlay: FlowElementTextOverlay,
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  custom: string | undefined,
  defaultEnglish: string,
  defaultTranslated: string,
): string {
  const hasCustom = !!custom && custom !== defaultEnglish;
  const canonical = hasCustom ? custom : defaultEnglish;
  const displayed = flowElementDisplayText(
    overlay,
    elementId,
    field,
    canonical,
  );
  if (displayed !== canonical) {
    return displayed;
  }
  return hasCustom ? canonical : defaultTranslated;
}

export function collectFlowElementIds(
  flows: (ReturnFlow | undefined)[],
): string[] {
  return flowElementIdsOfSteps(flows.flatMap((flow) => flow?.steps ?? []));
}

export function useFlowElementText(): (
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  text: string,
) => string {
  const overlay = useContext(FlowElementTextOverlayContext);
  return useCallback(
    (elementId, field, text) =>
      flowElementDisplayText(overlay, elementId, field, text),
    [overlay],
  );
}
